/**
 * SkillDedupConfig - Reusable dedup configuration form component.
 * Used in both global settings drawer and per-skill edit form.
 */

import React from 'react';
import { useI18n } from '@/i18n';

interface SkillDedupConfigProps {
  /** Base path prefix for field names (e.g., "" for global, "dedup_" for per-skill) */
  fieldPrefix?: string;
  /** Whether this is per-skill config (shows placeholder hints) */
  isPerSkill?: boolean;
  /** Global default values for placeholders (only for per-skill) */
  globalDefaults?: {
    data_query_hours?: number;
    report_window_hours?: number;
    max_per_window?: number;
    output_preview_length?: number;
    show_user_query?: boolean;
    query_preview_length?: number;
  };
  /** Current values */
  values: {
    data_query_hours?: number | null;
    report_window_hours?: number | null;
    max_per_window?: number | null;
    output_preview_length?: number | null;
    show_user_query?: boolean | null;
    query_preview_length?: number | null;
  };
  /** Change handler */
  onChange: (field: string, value: number | boolean | null) => void;
  /** Whether the form is disabled */
  disabled?: boolean;
}

const DEFAULT_VALUES = {
  data_query_hours: 168,
  report_window_hours: 24,
  max_per_window: 1,
  output_preview_length: 400,
  show_user_query: true,
  query_preview_length: 100,
};

/**
 * Reusable dedup configuration form component.
 * Used in both global settings drawer and per-skill edit form.
 */
export const SkillDedupConfig: React.FC<SkillDedupConfigProps> = ({
  fieldPrefix = '',
  isPerSkill = false,
  globalDefaults = {},
  values,
  onChange,
  disabled = false,
}) => {
  const { messages } = useI18n();
  const skills = messages.agentConfig.skills;
  // Filter out undefined/null values from globalDefaults before merging
  // to prevent them from overriding DEFAULT_VALUES
  const filteredGlobalDefaults = Object.fromEntries(
    Object.entries(globalDefaults).filter(([, v]) => v != null)
  );
  const defaults = { ...DEFAULT_VALUES, ...filteredGlobalDefaults };

  const getFieldName = (field: string) => `${fieldPrefix}${field}`;

  const getPlaceholder = (field: keyof typeof DEFAULT_VALUES) => {
    const defaultLabel = skills?.default || '默认';
    return isPerSkill ? `${defaultLabel}: ${defaults[field]}` : '';
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Data Query Hours */}
        <div>
          <label htmlFor={getFieldName('data_query_hours')} className="block text-sm font-medium text-gray-700">
            {skills?.dataQueryHours || '数据查询范围'}
          </label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              id={getFieldName('data_query_hours')}
              value={values.data_query_hours ?? ''}
              onChange={(e) => {
                const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                onChange(getFieldName('data_query_hours'), val);
              }}
              min={1}
              max={8760}
              placeholder={getPlaceholder('data_query_hours')}
              disabled={disabled}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm disabled:opacity-50"
            />
            <span className="text-sm text-gray-500 whitespace-nowrap">{skills?.hours || '小时'}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{skills?.dataQueryHoursHint || '查询历史执行记录的时间范围'}</p>
        </div>

        {/* Report Window Hours */}
        <div>
          <label htmlFor={getFieldName('report_window_hours')} className="block text-sm font-medium text-gray-700">
            {skills?.reportWindowHours || '报告时效窗口'}
          </label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              id={getFieldName('report_window_hours')}
              value={values.report_window_hours ?? ''}
              onChange={(e) => {
                const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                onChange(getFieldName('report_window_hours'), val);
              }}
              min={1}
              max={720}
              placeholder={getPlaceholder('report_window_hours')}
              disabled={disabled}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm disabled:opacity-50"
            />
            <span className="text-sm text-gray-500 whitespace-nowrap">{skills?.hours || '小时'}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{skills?.reportWindowHoursHint || '每个时间窗口的大小'}</p>
        </div>

        {/* Max Per Window */}
        <div>
          <label htmlFor={getFieldName('max_per_window')} className="block text-sm font-medium text-gray-700">
            {skills?.maxPerWindow || '每窗口最大条数'}
          </label>
          <input
            type="number"
            id={getFieldName('max_per_window')}
            value={values.max_per_window ?? ''}
            onChange={(e) => {
              const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
              onChange(getFieldName('max_per_window'), val);
            }}
            min={1}
            max={10}
            placeholder={getPlaceholder('max_per_window')}
            disabled={disabled}
            className="w-full mt-1 rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm disabled:opacity-50"
          />
          <p className="text-xs text-gray-500 mt-1">{skills?.maxPerWindowHint || '每个时间窗口最多显示几条记录'}</p>
        </div>

        {/* Output Preview Length */}
        <div>
          <label htmlFor={getFieldName('output_preview_length')} className="block text-sm font-medium text-gray-700">
            {skills?.outputPreviewLength || '输出摘要长度'}
          </label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              id={getFieldName('output_preview_length')}
              value={values.output_preview_length ?? ''}
              onChange={(e) => {
                const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                onChange(getFieldName('output_preview_length'), val);
              }}
              min={50}
              max={2000}
              placeholder={getPlaceholder('output_preview_length')}
              disabled={disabled}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm disabled:opacity-50"
            />
            <span className="text-sm text-gray-500 whitespace-nowrap">{skills?.chars || '字符'}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{skills?.outputPreviewLengthHint || '历史输出内容的最大显示字符数'}</p>
        </div>

        {/* Show User Query */}
        <div>
          <label className="flex items-center gap-3 cursor-pointer mt-6">
            <input
              type="checkbox"
              id={getFieldName('show_user_query')}
              checked={values.show_user_query ?? defaults.show_user_query}
              onChange={(e) => onChange(getFieldName('show_user_query'), e.target.checked)}
              disabled={disabled}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">{skills?.showUserQuery || '显示用户问题'}</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-7">{skills?.showUserQueryHint || '在历史记录中显示用户的原始问题'}</p>
        </div>

        {/* Query Preview Length */}
        <div>
          <label htmlFor={getFieldName('query_preview_length')} className="block text-sm font-medium text-gray-700">
            {skills?.queryPreviewLength || '问题摘要长度'}
          </label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              id={getFieldName('query_preview_length')}
              value={values.query_preview_length ?? ''}
              onChange={(e) => {
                const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                onChange(getFieldName('query_preview_length'), val);
              }}
              min={20}
              max={500}
              placeholder={getPlaceholder('query_preview_length')}
              disabled={disabled}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm disabled:opacity-50"
            />
            <span className="text-sm text-gray-500 whitespace-nowrap">{skills?.chars || '字符'}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{skills?.queryPreviewLengthHint || '用户问题的最大显示字符数'}</p>
        </div>
      </div>
    </div>
  );
};

export default SkillDedupConfig;
